import { create } from 'zustand';

interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
}

export type SortMode = 'dueDate' | 'manual' | 'priority' | 'created' | 'completed';

interface UIState {
  sidebarOpen: boolean;
  activeListId: string | null;
  sortMode: SortMode;
  toasts: Toast[];
  toggleSidebar: () => void;
  setActiveList: (listId: string | null) => void;
  setSortMode: (mode: SortMode) => void;
  addToast: (message: string, type?: Toast['type']) => void;
  removeToast: (id: string) => void;
}

export const useUIStore = create<UIState>((set) => ({
  sidebarOpen: true,
  activeListId: null,
  sortMode: 'dueDate',
  toasts: [],

  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),

  setActiveList: (listId) => set({ activeListId: listId }),

  setSortMode: (mode) => set({ sortMode: mode }),

  addToast: (message, type = 'info') => {
    const id = crypto.randomUUID();
    set((s) => ({ toasts: [...s.toasts, { id, message, type }] }));
    setTimeout(() => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })), 4000);
  },

  removeToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

// ─── Mobile drawer <-> browser history integration ─────────────────────────
// On mobile the sidebar becomes an off-canvas drawer (see index.css). Opening
// it pushes a throwaway history entry so Android's back button/gesture closes
// the drawer instead of navigating the app away — closing it any other way
// (backdrop tap, nav item, toggle) pops that same entry so history stays
// clean and a second back press doesn't just land you back where you were.
let drawerHistoryEntryPushed = false;

export function openMobileSidebar(): void {
  if (!drawerHistoryEntryPushed) {
    window.history.pushState({ mobileSidebar: true }, '');
    drawerHistoryEntryPushed = true;
  }
  useUIStore.setState({ sidebarOpen: true });
}

export function closeMobileSidebar(): void {
  useUIStore.setState({ sidebarOpen: false });
  if (drawerHistoryEntryPushed) {
    drawerHistoryEntryPushed = false;
    window.history.back();
  }
}

// Use when the drawer is closing because the user navigated (tapped a nav
// item/list row) rather than explicitly dismissing it (backdrop tap). Popping
// history here would undo the navigation that was just pushed, so this just
// closes the drawer and drops the stale marker without touching history.
export function dismissMobileSidebarForNavigation(): void {
  drawerHistoryEntryPushed = false;
  useUIStore.setState({ sidebarOpen: false });
}

export function toggleMobileSidebar(): void {
  if (useUIStore.getState().sidebarOpen) closeMobileSidebar();
  else openMobileSidebar();
}

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    if (useUIStore.getState().sidebarOpen) {
      drawerHistoryEntryPushed = false;
      useUIStore.setState({ sidebarOpen: false });
    }
  });
}
